import { z } from "zod";
import { ImageIcon, Package, Sparkle } from "@/components/icons";
import {
  Button,
  Checkbox,
  FieldContent,
  FieldError,
  FieldLabel,
  FieldLayout,
  FormActions,
  FormCheckbox,
  FormInput,
  FormRoot,
  FormTextarea,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  RHF,
  Separator,
  toast,
  zodResolver,
} from "@/components/kit";
import type { CategoryDTO } from "@/modules/products/domain/contracts";
import { CreateProductSchema } from "@/modules/products/domain/contracts";
import {
  ProductSlugSchema,
  slugifyProductName,
} from "@/shared/lib/product-slug";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";
import { ProductFormSection } from "./ProductFormSection";
import { useEffect, useRef } from "react";

const FormSchema = CreateProductSchema.extend({
  slug: ProductSlugSchema,
}).transform((data) => ({
  ...data,
  slug: data.slug?.trim() || slugifyProductName(data.name) || "product",
}));

export type ProductFormValues = z.infer<typeof FormSchema>;

type ProductFormProps = {
  categories: CategoryDTO[];
  defaultValues?: Partial<ProductFormValues>;
  onSubmit: (values: ProductFormValues) => Promise<void>;
  submitting?: boolean;
};

export function ProductForm({
  categories,
  defaultValues,
  onSubmit,
  submitting,
}: ProductFormProps) {
  const methods = RHF.useForm<ProductFormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      categoryIds:
        defaultValues?.categoryIds ??
        (defaultValues?.categoryIds === undefined && categories[0]?.id
          ? [categories[0].id]
          : []),
      name: defaultValues?.name ?? "",
      slug: defaultValues?.slug ?? "",
      description: defaultValues?.description ?? "",
      imageKey: defaultValues?.imageKey ?? "",
      lowStockThreshold: defaultValues?.lowStockThreshold ?? 3,
      isActive: defaultValues?.isActive ?? true,
    },
  });

  const slugTouchedRef = useRef(Boolean(defaultValues?.slug?.trim()));

  useEffect(() => {
    const subscription = methods.watch((values, info) => {
      if (info.name !== "name" || slugTouchedRef.current) return;
      const next = slugifyProductName(values.name ?? "");
      if (next) {
        methods.setValue("slug", next, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  function generateSlugFromName() {
    const next = slugifyProductName(methods.getValues("name") ?? "");
    if (!next) {
      toast.error("ໃສ່ຊື່ສິນຄ້າກ່ອນເພື່ອສ້າງ slug");
      return;
    }
    methods.setValue("slug", next, {
      shouldDirty: true,
      shouldValidate: true,
    });
    // ให้ slug ตามชื่อต่อได้ จนกว่าจะแก้ slug เอง
    slugTouchedRef.current = false;
  }

  return (
    <FormRoot
      methods={methods}
      onSubmit={onSubmit}
      onInvalid={() => {
        toast.error("ກະລຸນາກວດສອບຂໍ້ມູນໃນຟອມ — ລາຍການທີ່ຂີດແດງຍັງບໍ່ຄົບ");
      }}
      className="gap-6"
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,300px)_1fr]">
        <ProductFormSection
          icon={ImageIcon}
          title="ຮູບສິນຄ້າ"
          description="ຮູບຫຼັກສຳລັບໃຫ້ລູກຄ້າເລືອກໃນແຊັດ"
          className="h-full"
        >
          <ImageKeyUploadField
            label=""
            keyPrefix="uploads/products"
            value={methods.watch("imageKey") ?? ""}
            onChange={(key) =>
              methods.setValue("imageKey", key, { shouldDirty: true })
            }
            className="w-full"
            aspectRatio="aspect-[4/5]"
          />
        </ProductFormSection>

        <ProductFormSection
          icon={Package}
          title="ຂໍ້ມູນພື້ນຖານ"
          description="ຊື່, ຫມວດຫມູ່ ແລະ ລາຍລະອຽດສິນຄ້າ"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <RHF.Controller
              name="categoryIds"
              control={methods.control}
              render={({ field, fieldState }) => (
                <FieldLayout
                  data-invalid={fieldState.invalid || undefined}
                  className="sm:col-span-2"
                >
                  <FieldLabel>
                    ຫມວດຫມູ່ <span className="text-destructive">*</span>
                  </FieldLabel>
                  <FieldContent>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {categories.map((category) => {
                        const selected = field.value?.includes(category.id);
                        return (
                          <label
                            key={category.id}
                            htmlFor={`category-${category.id}`}
                            className="flex cursor-pointer items-center gap-2 rounded-xl border border-secondary/15 bg-background/60 px-3 py-2"
                          >
                            <Checkbox
                              id={`category-${category.id}`}
                              checked={selected}
                              onCheckedChange={(checked) => {
                                const current = field.value ?? [];
                                if (checked === true) {
                                  field.onChange([...current, category.id]);
                                  return;
                                }
                                field.onChange(
                                  current.filter((id) => id !== category.id),
                                );
                              }}
                            />
                            <span className="text-sm">{category.name}</span>
                          </label>
                        );
                      })}
                    </div>
                    {fieldState.error ? (
                      <FieldError>{fieldState.error.message}</FieldError>
                    ) : null}
                  </FieldContent>
                </FieldLayout>
              )}
            />
            <FormInput name="name" label="ຊື່ສິນຄ້າ" requiredMark />
            <RHF.Controller
              name="slug"
              control={methods.control}
              render={({ field, fieldState }) => (
                <FieldLayout
                  data-invalid={fieldState.invalid || undefined}
                  className="sm:col-span-2"
                >
                  <FieldLabel htmlFor="product-slug">
                    Slug (URL ແຄັດຕາລັອກ)
                  </FieldLabel>
                  <FieldContent>
                    <InputGroup>
                      <InputGroupAddon>
                        <InputGroupText>/catalog/</InputGroupText>
                      </InputGroupAddon>
                      <InputGroupInput
                        id="product-slug"
                        placeholder="handmade-yarn"
                        aria-invalid={fieldState.invalid}
                        {...field}
                        onChange={(event) => {
                          slugTouchedRef.current = true;
                          field.onChange(event);
                        }}
                      />
                      <InputGroupAddon align="inline-end">
                        <InputGroupButton
                          type="button"
                          size="xs"
                          variant="secondary"
                          aria-label="ສ້າງ slug ຈາກຊື່ສິນຄ້າ"
                          onClick={generateSlugFromName}
                        >
                          <Sparkle className="size-3.5" />
                          ສ້າງ
                        </InputGroupButton>
                      </InputGroupAddon>
                    </InputGroup>
                    <p className="mt-1.5 text-muted-foreground text-xs">
                      ສ້າງອັດຕະໂນມັດຈາກຊື່ — ກົດ «ສ້າງ» ເພື່ອອັບເດດໃໝ່ ຫຼືແກ້ເອງໄດ້
                    </p>
                    {fieldState.error ? (
                      <FieldError>{fieldState.error.message}</FieldError>
                    ) : null}
                  </FieldContent>
                </FieldLayout>
              )}
            />
          </div>

          <FormTextarea
            name="description"
            label="ຄຳອະທິບາຍ"
            placeholder="ລາຍລະອຽດສຳລັບອ້າງອີງເວລາຕອບລູກຄ້າ..."
            rows={3}
            className="min-h-20 resize-y"
          />

          <Separator className="bg-secondary/20" />

          <div className="grid gap-4 sm:grid-cols-2">
            <FormInput
              name="lowStockThreshold"
              label="ເຕືອນໃກ້ໝົດ (ຄ່າເລີ່ມຕົ້ນ)"
              type="number"
              hint="ໃຊ້ເມື່ອເພີ່ມຕົວເລືອກໃໝ່ — ປັບແຍກໄດ້ຕໍ່ຕົວເລືອກ"
            />
            <div className="flex items-end pb-1">
              <FormCheckbox name="isActive" label="ເປີດຂາຍ (ສະແດງໃນຄລັງ)" />
            </div>
          </div>
        </ProductFormSection>
      </div>

      <div className="sticky bottom-0 z-10 -mx-1 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-secondary/25 bg-card/95 p-4 shadow-lg shadow-secondary/10 backdrop-blur-sm sm:static sm:mx-0 sm:bg-transparent sm:p-0 sm:shadow-none sm:backdrop-blur-none">
        <p className="ml-2 text-muted-foreground text-sm">ກວດສອບຂໍ້ມູນກ່ອນບັນທຶກ</p>
        <FormActions className="pt-0">
          <Button
            type="submit"
            className="rounded-full px-6 shadow-sm"
            isLoading={submitting}
          >
            ບັນທຶກສິນຄ້າ
          </Button>
        </FormActions>
      </div>
    </FormRoot>
  );
}
